import { useCallback, useMemo, useState } from "react"
import styled, { css } from "styled-components"

import Collapse from "@forento/shared-client/components/Collapse"

import { usePlatform } from "~/contexts/PlatformContext"
import withCommunityAuthCheck from "~/hocs/withCommunityAuthCheck"
import Button from "~/themes/school/components/Button"
import ContentSearch from "~/themes/school/components/ContentSearch"
import { ChevronIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import PageHeader, { PageHeaderGroup, PageTitle } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import Post from "~/themes/school/components/Post"
import CreatePostModal from "~/themes/school/pages/community/CommunityPage/CreatePostModal"
import { useTranslation } from "~/translations"
import { query } from "~/utilities/trpc"

const CommunityPage = withCommunityAuthCheck(() => {
	const platform = usePlatform().platform
	const t = useTranslation()

	const [isCreatePostModalOpen, setCreatePostModalOpen] = useState(false)
	const [isMobileChannelsOpen, setMobileChannelsOpen] = useState(false)
	const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null)
	const [searchItems, setSearchItems] = useState<number[]>()
	const [postContents, setPostContents] = useState<Record<number, string>>({})

	const channels = query.community.listPersonalizedChannels.useQuery()
	const posts = query.community.listPersonalizedPostsByChannel.useQuery(selectedChannelId)

	const selectedChannel = channels.data?.find(channel => channel.id === selectedChannelId)

	const contentSearchItems = useMemo(
		() =>
			posts.data?.map(post => ({
				id: post.id,
				title: post.title,
				content: postContents[post.id] ?? "",
			})) ?? [],
		[posts.data, postContents],
	)

	const keys = useMemo(() => ["title" as const, "content" as const], [])

	const handlePostContentLoaded = useCallback((postId: number, text: string) => {
		setPostContents(current => ({ ...current, [postId]: text }))
	}, [])

	return (
		<Layout>
			{selectedChannel && (
				<CreatePostModal
					channel={selectedChannel}
					isOpen={isCreatePostModalOpen}
					onClose={() => setCreatePostModalOpen(false)}
					onCreate={posts.refetch}
				/>
			)}
			<PageHeader $matchPageWidth>
				<PageTitle>{t("community.title")}</PageTitle>
				<PageHeaderGroup>
					<ContentSearch keys={keys} items={contentSearchItems} onItemsToShowChange={setSearchItems} />
					{selectedChannel?.userAccess === "post" && (
						<CreatePostButton onClick={() => setCreatePostModalOpen(true)}>
							{t("community.post.create")}
						</CreatePostButton>
					)}
				</PageHeaderGroup>
			</PageHeader>
			{platform.content.multipleChannels &&
				(channels.error ? (
					<p>{t("failedToLoad")}</p>
				) : channels.data === undefined ? (
					<PartialLoadingPage />
				) : (
					<>
						<MobileChannels>
							<MobileChannelsHeader>
								{selectedChannel ? <strong>{selectedChannel.name}</strong> : "No channel selected"}
								<ToggleMobileChannelsButton onClick={() => setMobileChannelsOpen(current => !current)}>
									<ToggleMobileChannelsButtonIcon direction="down" $isOpen={isMobileChannelsOpen} />
								</ToggleMobileChannelsButton>
							</MobileChannelsHeader>
							<Collapse isCollapsed={!isMobileChannelsOpen}>
								<MobileChannelsContent>
									{channels.data.map(channel => (
										<Channel
											key={channel.id}
											variant={selectedChannelId === channel.id ? "primary" : "secondary"}
											onClick={() => {
												setSelectedChannelId(channel.id)
												setMobileChannelsOpen(false)
											}}
										>
											{channel.name}
										</Channel>
									))}
								</MobileChannelsContent>
							</Collapse>
						</MobileChannels>
						<DesktopChannels>
							{channels.data.map(channel => (
								<Channel
									key={channel.id}
									variant={selectedChannelId === channel.id ? "primary" : "secondary"}
									onClick={() => setSelectedChannelId(channel.id)}
								>
									{channel.name}
								</Channel>
							))}
						</DesktopChannels>
					</>
				))}
			{posts.error ? (
				<p>{t("failedToLoad")}</p>
			) : posts.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<Posts>
					{(searchItems !== undefined
						? posts.data
								.filter(x => searchItems.includes(x.id))
								.sort((a, b) => searchItems.indexOf(b.id) - searchItems.indexOf(a.id))
						: posts.data
					).map(x => (
						<Post
							key={x.id}
							post={x}
							onChange={posts.refetch}
							onPostContentLoaded={handlePostContentLoaded}
						/>
					))}
				</Posts>
			)}
		</Layout>
	)
})

const CreatePostButton = styled(Button).attrs({ variant: "primary" })`
	flex: 0 0 auto;
`

const MobileChannels = styled.div`
	width: 100%;
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;
	margin-bottom: 24px;

	@media (min-width: 1000px) {
		display: none;
	}
`

const MobileChannelsHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`

const ToggleMobileChannelsButton = styled(Button)`
	width: 24px;
	height: 24px;
`

const ToggleMobileChannelsButtonIcon = styled(ChevronIcon)<{ $isOpen: boolean }>`
	width: 100%;
	height: 100%;
	transition: transform 0.15s;

	${props =>
		props.$isOpen &&
		css`
			transform: scaleY(-1);
		`}
`

const MobileChannelsContent = styled.div`
	padding-top: 24px;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
`

const DesktopChannels = styled.div`
	max-width: 1400px;
	background-color: white;
	box-shadow: 0px 4px 30px 0px rgba(223, 232, 255, 0.25);
	padding: 24px;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 16px;
	margin-bottom: 24px;

	@media (max-width: 1000px) {
		display: none;
	}
`

const Channel = styled(Button)`
	white-space: nowrap;
`

const Posts = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`

export default CommunityPage
